import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Button, ButtonBar } from '@/components/Buttons';
import { ValidatingInput, ValidatingTextArea } from '@/components/forms/validation';
import { FileUpload, Modal, ModalBody, ModalHeader, ModalFooter } from '@ygtechnologies/bygr-frontend-component-library';
import React, { useState } from 'react';
import { StandardReply, StandardReplyAttachmentDto } from '@/types';
import { classnames } from '@/utils/html';
import theme from './StandardReplies.module.scss';
import MandatoryIndicator from '@/components/forms/MandatoryIndicator';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import {
    editStandardReply,
    addStandardReply,
    StandardReplyUpdateOptionsAttachment,
    StandardReplyUpdateOptions,
} from '@/services/standard-replies';
import { useMergeState } from '@/utils/hooks';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faTrashAlt } from '@fortawesome/pro-light-svg-icons';

type EditStandardReplyModalProps = {
    isOpen: boolean;
    toggle: () => void;
    onSave(standardReply: StandardReply): void;
    standardReply?: StandardReply;
    onDeleteClick?(): void;
};

function getEmptyState(profileId: number): StandardReplyUpdateOptions {
    return {
        profileId,
        title: '',
        text: '',
        comment: '',
        attachments: [],
    };
}

function toUploadOptions(profileId: number, standardReply: StandardReply): StandardReplyUpdateOptions {
    return {
        profileId,
        text: standardReply.text,
        title: standardReply.title,
        comment: standardReply.comment,
        attachments: null,
    };
}

type PendingAttachment = StandardReplyUpdateOptionsAttachment | StandardReplyAttachmentDto;

const EditStandardReplyModal: React.FC<EditStandardReplyModalProps> = ({
    isOpen,
    toggle,
    standardReply,
    onSave,
    onDeleteClick,
}) => {
    const profileId = useSelector((state: IState) => state.global.activeProfile!.id);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [unsavedStandardReply, mergeUnsavedStandardReply, setUnsavedStandardReply] = useMergeState(
        getEmptyState(profileId)
    );

    const [attachments, setAttachments] = useState<PendingAttachment[]>([]);

    const reset = () => {
        setError(false);
        setSaving(false);

        if (standardReply) {
            setUnsavedStandardReply(toUploadOptions(profileId, standardReply));
            setAttachments(standardReply.attachments);
        } else {
            setUnsavedStandardReply(getEmptyState(profileId));
            setAttachments([]);
        }
    };

    const handleDeleteAttachment = (att: PendingAttachment) => {
        setAttachments((prev) => prev.filter((x) => x !== att));
    };

    const validate = async () => {
        if (unsavedStandardReply.title && unsavedStandardReply.text) {
            setSaving(true);
            var result: StandardReply | null = null;
            var data = {
                ...unsavedStandardReply,
                attachments: attachments.map<StandardReplyUpdateOptionsAttachment>((x) => ({
                    id: x.id,
                    file: 'file' in x ? x.file : null,
                })),
            };

            try {
                if (standardReply && standardReply.id) {
                    result = await editStandardReply(standardReply.id, data);
                } else {
                    result = await addStandardReply(data);
                }

                onSave(result);
                toggle();
            } finally {
                setSaving(false);
            }
        }

        setError(true);
    };

    return (
        <Modal closeOnEscape={false} isOpen={isOpen} toggle={toggle} onOpen={reset} size="lg">
            <ModalHeader>
                <Trans>Edit reply</Trans>
            </ModalHeader>
            <ModalBody>
                <div className={theme.inputContainer}>
                    <label className={theme.label}>
                        <Trans>Name</Trans>
                        <MandatoryIndicator />
                    </label>
                    <ValidatingInput
                        required
                        className={classnames(
                            'form-control',
                            { [theme.error]: error && !unsavedStandardReply.title },
                            theme.input
                        )}
                        value={unsavedStandardReply.title}
                        onChange={(e) => mergeUnsavedStandardReply({ title: e.target.value })}
                        placeholder={t`Make a short yet descriptive name of a reply`}
                    />
                    {error && !unsavedStandardReply.title && (
                        <div className={theme.error}>
                            <Trans>Name required</Trans>
                        </div>
                    )}
                </div>
                <div className={theme.inputContainer}>
                    <label className={theme.label}>
                        <Trans>Content body</Trans>
                        <MandatoryIndicator />
                    </label>
                    <ValidatingTextArea
                        required
                        className={classnames(
                            'form-control',
                            { [theme.error]: error && !unsavedStandardReply.text },
                            theme.input
                        )}
                        value={unsavedStandardReply.text}
                        onChange={(e) => mergeUnsavedStandardReply({ text: e.target.value })}
                        placeholder={t`Main body`}
                    />

                    {error && !unsavedStandardReply.text && (
                        <div className={theme.error}>
                            <Trans>Content body required</Trans>
                        </div>
                    )}
                </div>

                <div className={theme.inputContainer}>
                    <label className={theme.label}>
                        <Trans>Attachment (optional)</Trans>
                    </label>
                    <FileUpload
                        containerClassName={theme.fileUpload}
                        onDrop={(f) => {
                            // Helps with certain fileTypes (.HEIC)
                            // if (!f?.[0]) return;

                            try {
                                const newFiles = f.map<StandardReplyUpdateOptionsAttachment>((x) => ({
                                    id: null,
                                    file: new FileData(x),
                                }));
                                setAttachments((prev) => [...prev, ...newFiles]);
                            } catch (error) {
                                console.error('Error:', error);
                            }
                        }}
                    />

                    {attachments.map((att, i) => (
                        <div key={i} className={theme.formFileContainer}>
                            <div className={theme.formFileInfo}>
                                <div className={theme.formFileIcon}>
                                    <FontAwesomeIcon icon={faFile} />
                                </div>
                                <div className={theme.formFileName}>
                                    {'file' in att ? att.file?.fileName : att.fileMetadata.fileName}
                                </div>
                            </div>
                            <Button
                                color="danger"
                                icon={faTrashAlt}
                                onClick={() => handleDeleteAttachment(att)}
                                className={theme.attachmentRemoveButton}
                            />
                        </div>
                    ))}
                </div>

                <div className={theme.inputContainer}>
                    <label className={theme.label}>
                        <Trans>Add comment (optional)</Trans>
                    </label>
                    <ValidatingInput
                        required
                        className={classnames('form-control', theme.input)}
                        value={unsavedStandardReply.comment}
                        onChange={(e) => mergeUnsavedStandardReply({ comment: e.target.value })}
                        placeholder={t`Here you can add an explanation of when to use the reply`}
                    />
                </div>
            </ModalBody>
            <ModalFooter>
                <ButtonBar className={theme.buttonBar}>
                    {onDeleteClick && (
                        <Button onClick={onDeleteClick} disabled={saving} color="tertiary">
                            <Trans>Delete</Trans>
                        </Button>
                    )}
                    <ButtonBar.Right>
                        <Button onClick={toggle} color="tertiary">
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button onClick={validate} loading={saving} color="primary">
                            <Trans>Save changes</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </ModalFooter>
        </Modal>
    );
};
export default EditStandardReplyModal;
